import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Gift, Wallet, Sparkles, Users, FlaskConical, Check, Crown, ArrowRight, HeartHandshake, Star } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const IMG = {
  heroMain: "https://images.unsplash.com/photo-1689147782410-8630824fffa6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  heroSecond: "https://images.unsplash.com/photo-1619002117199-47c7f0427d21?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  price: "https://images.unsplash.com/photo-1766238955752-0200601334ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  drops: "https://images.unsplash.com/photo-1788103646086-6f669fdad463?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  wallet: "https://images.unsplash.com/photo-1758368406239-4702a73f3705?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  lab: "https://images.unsplash.com/photo-1773070309549-8123488410d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  flatlay: "https://images.unsplash.com/photo-1788104139165-86ba1ea4043f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  community: "https://images.unsplash.com/photo-1607960402358-a5ceb04ebeb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  joy: "https://images.unsplash.com/photo-1655490606874-0c9776443278?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

const BENEFITS = [
  [Tag, "Member Prices", "Exclusive pricing on selected products, only for Club members.", IMG.price],
  [Gift, "Club Drops", "Special products and collections reserved for members.", IMG.drops],
  [Wallet, "Suvikka Wallet", "Earn eligible rewards and use them on future orders.", IMG.wallet],
  [Sparkles, "Early Access", "See selected launches before general customers.", IMG.flatlay],
  [Users, "Club Community", "Share experiences, discoveries and recommendations.", IMG.community],
  [FlaskConical, "Secret Lab", "Vote on what Suvikka makes next.", IMG.lab],
];

const STEPS = [
  ["01", "Join the Club", "One annual membership unlocks the full experience."],
  ["02", "Shop & Earn", "Member prices, wallet rewards and early access on selected drops."],
  ["03", "Belong", "A community of Sakhis discovering what's next, together."],
];

export default function Club() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState({ club_price: 99, club_duration_days: 365, club_welcome_reward: 50 });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/settings/public").then((r) => setCfg(r.data)).catch(() => {});
    if (user) API.get("/club/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const join = async () => {
    if (!user) { nav("/login", { state: { from: "/club" } }); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/club/create-order");
      if (data.mode === "razorpay") {
        const rzp = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: "INR", order_id: data.order_id,
          name: "Suvikka Club", description: "Annual Membership",
          prefill: { name: user.name, email: user.email, contact: user.mobile || "" },
          theme: { color: "#6A1B29" },
          handler: async (res) => {
            try {
              const a = await API.post("/club/activate", { razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
              toast.success(`Welcome to the Club! +${inr(a.data.welcome_reward)} wallet 💗`);
              setStatus({ is_member: true, club_expires_at: a.data.club_expires_at });
            } catch (e) { toast.error(errMsg(e)); }
          },
        });
        rzp.open();
      } else {
        const a = await API.post("/club/activate", {});
        toast.success(`Welcome to the Club! +${inr(a.data.welcome_reward)} wallet 💗`);
        setStatus({ is_member: true, club_expires_at: a.data.club_expires_at });
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const member = status?.is_member;
  const joinBtn = (testid, extra = "") => member ? (
    <span className={`inline-flex items-center gap-2 bg-[#6A1B29] text-white font-semibold px-8 py-4 rounded-full ${extra}`} data-testid={testid}><Check className="w-5 h-5" /> You're a Club Member 💗</span>
  ) : (
    <button onClick={join} disabled={busy} data-testid={testid} className={`bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold px-10 py-4 rounded-full text-lg transition-transform hover:scale-105 ${extra}`}>{busy ? "Please wait…" : "JOIN SUVIKKA CLUB"}</button>
  );

  return (
    <div className="motif-ivory" data-testid="club-page">
      <Seo title="Suvikka Club | Member Prices, Wallet Rewards & Early Access" description="Join Suvikka Club — exclusive member prices, Club Drops, wallet rewards, early access, community and product voting. Everything she loves, more exclusively." canonicalPath="/club" image={IMG.heroMain} />

      {/* HERO — editorial split */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#2A1418] via-[#3d1a24] to-[#1C1917] text-white">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(#F6A5BE 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE] inline-flex items-center gap-2"><Crown className="w-4 h-4" /> Suvikka Club</p>
            <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-4 leading-[1.08]">This Isn't Just Shopping.<br /><span className="text-[#F6A5BE] italic">It's More Exclusive.</span></h1>
            <p className="text-stone-300 mt-5 max-w-lg text-base sm:text-lg leading-relaxed">Your access to exclusive finds, member prices, wallet rewards and a community that loves discovering what's next.</p>
            <div className="mt-8 inline-flex flex-col items-start bg-white/10 border border-white/15 rounded-3xl px-8 py-6 backdrop-blur" data-testid="club-price-card">
              <p className="text-[11px] tracking-[0.25em] uppercase text-stone-300">Annual Membership</p>
              <p className="font-display text-5xl font-bold mt-1">{inr(cfg.club_price)}<span className="text-lg font-normal text-stone-400">/year</span></p>
              {cfg.club_welcome_reward > 0 && <p className="text-sm text-[#F6A5BE] mt-1">+{inr(cfg.club_welcome_reward)} welcome wallet reward</p>}
            </div>
            <div className="mt-7">{joinBtn("club-join-button")}</div>
            <p className="text-[11px] text-stone-400 mt-4 max-w-lg">Membership benefits are subject to availability and applicable terms. No guaranteed savings are promised.</p>
          </div>
          <div className="relative hidden sm:block" data-testid="club-hero-visual">
            <div className="relative mx-auto max-w-md">
              <img src={IMG.heroMain} alt="A Suvikka Sakhi" className="w-full aspect-[3/4] object-cover rounded-[2rem] rotate-2 shadow-2xl ring-1 ring-white/20" />
              <img src={IMG.heroSecond} alt="Elegant details" className="absolute -bottom-10 -left-14 w-44 aspect-[4/5] object-cover rounded-3xl -rotate-6 shadow-2xl ring-4 ring-[#2A1418]" />
              <div className="absolute -top-5 -right-4 bg-[#6A1B29] text-white rounded-2xl px-5 py-3 shadow-xl rotate-3">
                <p className="text-[10px] uppercase tracking-wider text-white/80">Members get</p>
                <p className="font-display font-bold text-lg leading-tight">Prices · Drops · Rewards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS — bento grid with imagery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">It's More Than A Membership.</h2>
        <p className="text-stone-500 text-center mt-3">Shop smarter. Earn more. Be part of a community that celebrates you.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {BENEFITS.map(([Icon, title, desc, img], i) => (
            <div key={title} className="group bg-white border border-[#EADBD8] rounded-2xl overflow-hidden shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all duration-300" data-testid={`club-benefit-${i}`}>
              <div className="relative h-40 overflow-hidden">
                <img src={img} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute bottom-3 left-3 inline-flex p-2.5 rounded-xl bg-white/95 text-[#6A1B29] shadow"><Icon className="w-5 h-5" /></span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-stone-900">{title}</h3>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">How The Club Works</h2>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {STEPS.map(([n, t, d], i) => (
              <div key={n} className="relative bg-[#FFF0ED] rounded-2xl p-7 border border-[#EADBD8] shadow-soft" data-testid={`club-step-${i}`}>
                <span className="font-display text-5xl font-bold text-[#F6A5BE]">{n}</span>
                <h3 className="font-display text-xl font-semibold text-stone-900 mt-3">{t}</h3>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DROPS + LAB — editorial bands */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <Link to="/drops" className="group relative rounded-3xl overflow-hidden min-h-[320px] flex items-end" data-testid="club-drops-band">
            <img src={IMG.drops} alt="Club Drops" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1418]/90 via-[#2A1418]/30 to-transparent" />
            <div className="relative p-8 text-white">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F6A5BE]">Members Only</p>
              <h3 className="font-display text-3xl font-semibold mt-2">Club Drops</h3>
              <p className="text-stone-300 text-sm mt-2 max-w-sm">Special products and collections, released for members first.</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white bg-[#6A1B29] px-4 py-2 rounded-full">Explore Drops <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
          <Link to="/secret-lab" className="group relative rounded-3xl overflow-hidden min-h-[320px] flex items-end" data-testid="club-lab-band">
            <img src={IMG.lab} alt="Secret Lab" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1418]/90 via-[#2A1418]/30 to-transparent" />
            <div className="relative p-8 text-white">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F6A5BE]">You Decide</p>
              <h3 className="font-display text-3xl font-semibold mt-2">The Secret Lab</h3>
              <p className="text-stone-300 text-sm mt-2 max-w-sm">Vote on formulas, shades and ideas before Suvikka makes them real.</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white bg-[#6A1B29] px-4 py-2 rounded-full">Enter the Lab <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* COMMUNITY band */}
      <section className="bg-[#2A1418] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4" data-testid="club-community-visual">
            <img src={IMG.community} alt="Sakhis together" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-3xl mt-8" />
            <img src={IMG.joy} alt="Celebration" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-3xl" />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE] inline-flex items-center gap-2"><HeartHandshake className="w-4 h-4" /> Suvikka Sakhi</p>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-4 leading-tight">A Club With <span className="text-[#F6A5BE] italic">A Heartbeat.</span></h2>
            <p className="text-stone-300 mt-5 leading-relaxed">Behind every drop and every price is a community of Sakhis — women sharing discoveries, stories and recommendations. Your membership plugs you straight into that circle.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/suvikka-sakhi" className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3.5 rounded-full transition-transform hover:scale-105" data-testid="club-community-cta">Meet the Community <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/community" className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors" data-testid="club-feed-cta"><Star className="w-4 h-4" /> See the Feed</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="club-final-cta">
        <img src={IMG.flatlay} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2A1418]/80" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center text-white">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F6A5BE]"><Crown className="w-4 h-4 inline mr-1.5" /> One Year. One Circle.</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-4 leading-tight">Everything She Loves.<br /><span className="text-[#F6A5BE] italic">More Exclusively.</span></h2>
          <p className="text-stone-300 mt-5 max-w-xl mx-auto">Member prices, drops, wallet rewards and the Sakhi community — all for {inr(cfg.club_price)} a year.</p>
          <div className="mt-8">{joinBtn("club-join-button-2", "bg-white !text-stone-900 hover:!bg-stone-100")}</div>
        </div>
      </section>
    </div>
  );
}
